import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getCountryFlagUrl, getEventColor } from "@/utils/countryUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type VacationRequest = Tables<"vacation_requests"> & {
  profiles: {
    full_name: string | null;
    avatar_url: string | null;
  };
};

type BusyPeriod = Tables<"busy_periods">;

const generateColor = (index: number) => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD',
    '#D4A5A5', '#9B59B6', '#3498DB', '#E67E22', '#1ABC9C'
  ];
  return colors[index % colors.length];
};

export function TeamCalendar() {
  
  const getAvatarUrl = (fileName: string | null) => {
    if (!fileName) return '';
    const { data } = supabase.storage
      .from('public-media')
      .getPublicUrl(`${fileName}?t=${new Date().getTime()}`);
    return data.publicUrl;
  };

  const { data: approvedVacations } = useQuery({
    queryKey: ["all_approved_vacations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vacation_requests")
        .select(`
          *,
          profiles (
            full_name,
            avatar_url
          )
        `)
        .eq("status", "approved");

      if (error) throw error;
      return data as VacationRequest[];
    },
  });

  const { data: busyPeriods } = useQuery({
    queryKey: ["busy_periods"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("busy_periods")
        .select("*");

      if (error) throw error;
      return data as BusyPeriod[];
    },
  });

  // Group vacations by user
  const userVacations = approvedVacations?.reduce((acc, vacation) => {
    const userId = vacation.user_id;
    if (!acc[userId]) {
      acc[userId] = {
        user: vacation.profiles,
        vacations: [],
        color: ''
      };
    }
    acc[userId].vacations.push(vacation);
    return acc;
  }, {} as Record<string, { user: VacationRequest['profiles']; vacations: VacationRequest[]; color: string }>);

  // Assign colors to users
  Object.keys(userVacations || {}).forEach((userId, index) => {
    if (userVacations) {
      userVacations[userId].color = generateColor(index);
    }
  });

  // Create modifiers for the calendar
  const modifiers: Record<string, any[]> = {};
  
  // Add vacation modifiers
  Object.entries(userVacations || {}).forEach(([userId, { vacations, color }]) => {
    vacations.forEach(vacation => {
      const startDate = new Date(vacation.start_date);
      const endDate = new Date(vacation.end_date);
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      let currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = currentDate.toISOString().split('T')[0];
        if (!modifiers[dateKey]) {
          modifiers[dateKey] = [];
        }
        modifiers[dateKey].push({
          color,
          userId,
          type: 'vacation',
          userName: userVacations[userId].user.full_name
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });
  });

  // Add busy period modifiers with unique colors per event
  busyPeriods?.forEach((period) => {
    const startDate = new Date(period.start_date);
    const endDate = new Date(period.end_date);
    const eventColor = getEventColor(period.country || period.id);
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      if (!modifiers[dateKey]) {
        modifiers[dateKey] = [];
      }
      modifiers[dateKey].push({
        type: 'busy',
        description: period.description,
        country: period.country,
        eventColor
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });


  return (
    <div className="w-full">
      <Calendar
        mode="multiple"
        selected={[]}
        modifiers={modifiers}
        className="rounded-md border p-4 w-full bg-white"
        classNames={{
          months: "flex flex-row w-full gap-8",
          month: "flex-1 space-y-4",
          table: "w-full border-collapse space-y-1",
          head_row: "flex justify-around",
          head_cell: "text-muted-foreground rounded-md flex-1 text-center font-normal text-sm",
          row: "flex w-full mt-2 justify-around",
          cell: "relative flex-1 text-center text-sm focus-within:relative focus-within:z-20 border border-border/60 rounded-md",
          day: "h-14 w-full p-0 font-normal rounded-md inline-flex items-center justify-center text-sm hover:bg-accent hover:text-accent-foreground",
          caption_label: "text-lg font-medium",
        }}
        showOutsideDays={false}
        numberOfMonths={2}
        components={{
          DayContent: ({ date }) => {
            const dateKey = date.toISOString().split('T')[0];
            const dateModifiers = modifiers[dateKey] || [];
            const vacationUsers = dateModifiers.filter(mod => mod.type === 'vacation');
            const busyPeriod = dateModifiers.find(mod => mod.type === 'busy');
            const hasAvatars = vacationUsers.length > 0;
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                     <div 
                      className="relative w-full h-14 flex items-center justify-center rounded-md"
                      style={busyPeriod ? { backgroundColor: busyPeriod.eventColor?.bg } : {}}
                    >
                      {busyPeriod?.country && (
                        <img
                          src={getCountryFlagUrl(busyPeriod.country)}
                          alt=""
                          className="absolute top-0 left-0.5 h-4 w-4 rounded-full object-cover z-30"
                        />
                      )}
                      {hasAvatars ? (
                        <>
                          <div className="absolute top-0.5 right-1 z-30 text-[10px] text-muted-foreground font-medium">{date.getDate()}</div>
                          <div className="flex -space-x-2 z-20">
                            {vacationUsers.slice(0, 3).map((modifier: any, index: number) => {
                              const userData = userVacations?.[modifier.userId];
                              return (
                                <Avatar key={`${modifier.userId}-${index}`} className="h-8 w-8 border-2 border-background">
                                  <AvatarImage 
                                    src={userData?.user.avatar_url ? getAvatarUrl(userData.user.avatar_url) : undefined}
                                    className="object-cover"
                                  />
                                  <AvatarFallback className="text-[9px] font-semibold" style={{ backgroundColor: modifier.color, color: 'white' }}>
                                    {modifier.userName?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                              );
                            })}
                            {vacationUsers.length > 3 && (
                              <div className="h-8 w-8 rounded-full bg-muted border-2 border-background flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                                +{vacationUsers.length - 3}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm">{date.getDate()}</span>
                      )}
                    </div>
                  </TooltipTrigger>
                  {(vacationUsers.length > 0 || busyPeriod) && (
                    <TooltipContent>
                      <div className="space-y-2">
                        {vacationUsers.length > 0 && (
                          <div>
                            <p className="font-semibold">On Vacation:</p>
                            {vacationUsers.map((user, index) => (
                              <p key={index}>{user.userName}</p>
                            ))}
                          </div>
                        )}
                        {busyPeriod && (
                          <div>
                            <p className="font-semibold text-destructive">Busy Period:</p>
                            <p>{busyPeriod.description}</p>
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          }
        }}
      />
    </div>
  );
}